// ProjectDescription.tsx
import { motion } from 'framer-motion';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Project } from '../../types/project';

interface ProjectDescriptionProps {
  project: Project;
}

export const ProjectDescription: React.FC<ProjectDescriptionProps> = ({
  project,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4 },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3,
        yoyo: Infinity,
      },
    },
  };

  const chevronVariants = {
    initial: { y: 0 },
    animate: {
      y: isHovering ? [0, -2, 0, 2, 0] : 0,
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <motion.div variants={itemVariants} className="relative pt-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3
            className="relative px-4 py-1.5 text-sm font-semibold
            before:absolute before:inset-0 before:bg-gradient-to-r 
            before:from-violet-500/10 before:to-blue-500/10 
            dark:before:from-violet-500/20 dark:before:to-blue-500/20
            before:rounded-lg before:-z-10
            after:absolute after:bottom-0 after:left-1 after:right-1 
            after:h-[2px] after:bg-gradient-to-r
            after:from-violet-500 after:to-blue-500
            text-gray-800 dark:text-gray-200"
          >
            <motion.span
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              Project Details
            </motion.span>
          </h3>
        </div>

        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          variants={buttonVariants}
          whileHover="hover"
          className="group flex items-center gap-2 px-3 py-1.5 rounded-lg
            bg-gradient-to-r from-violet-100 to-blue-100 
            dark:from-violet-900/40 dark:to-blue-900/40
            hover:from-violet-200 hover:to-blue-200
            dark:hover:from-violet-800/40 dark:hover:to-blue-800/40
            text-gray-700 dark:text-gray-300
            transition-colors duration-300"
        >
          <span className="text-sm font-medium">
            {isExpanded ? 'Show Less' : 'Read More'}
          </span>
          <motion.div
            variants={chevronVariants}
            initial="initial"
            animate="animate"
            className="transition-transform duration-300"
          >
            {isExpanded ? (
              <ChevronUp
                size={20}
                className="text-gray-600 dark:text-gray-400"
              />
            ) : (
              <ChevronDown
                size={20}
                className="text-gray-600 dark:text-gray-400"
              />
            )}
          </motion.div>
        </motion.button>
      </div>

      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? 'auto' : '4.5rem',
          overflow: 'hidden',
        }}
        transition={{ duration: 0.3 }}
        className={`relative ${!isExpanded ? 'mask-bottom' : ''}`}
      >
        {/* New layout structure */}
        <div className="space-y-4">
          {/* Overview - Full width */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Overview
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {project.description.overview}
            </p>
          </div>

          {/* Features & Challenges in two columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Features Section */}
            {project.description.features.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Key Features
                </h4>
                <ul className="space-y-1.5">
                  {project.description.features.map((feature, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2"
                    >
                      <span className="text-violet-500 mt-1">•</span>
                      <span className="leading-tight">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Challenges Section */}
            {project.description.challenges?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Challenges
                </h4>
                <ul className="space-y-1.5">
                  {project.description.challenges.map((challenge, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2"
                    >
                      <span className="text-blue-500 mt-1">•</span>
                      <span className="leading-tight">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {!isExpanded && (
        <div
          className="absolute bottom-0 left-0 right-0 h-8
          bg-gradient-to-t from-white dark:from-gray-800 to-transparent"
        />
      )}
    </motion.div>
  );
};
